import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AnimatedTitleAndDescriptionProps {
    title: React.ReactNode;
    children: React.ReactNode;
}

const AnimatedTitleAndDescription: React.FC<AnimatedTitleAndDescriptionProps> = ({ title, children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const sectionRef = useRef<HTMLDivElement | null>(null);
    const titleRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [titleVisible, setTitleVisible] = useState(false);

    useEffect(() => {
        const sectionObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    sectionObserver.disconnect();
                }
            },
            { threshold: 0.3 }
        );

        const titleObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setTitleVisible(true);
                    titleObserver.disconnect();
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) sectionObserver.observe(sectionRef.current);
        if (titleRef.current) titleObserver.observe(titleRef.current);

        return () => {
            sectionObserver.disconnect();
            titleObserver.disconnect();
        };
    }, []);

    return (
        <Box
            ref={sectionRef}
            display="flex"
            flexDirection="column"
            sx={{
                ...theme.applyStyles?.("dark", {}),
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0)' : 'translateX(-48px)',
                transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                    transform: 'none',
                },
            }}
        >
            <Box ref={titleRef}
                sx={{
                    opacity: titleVisible ? 1 : 0,
                    transform: titleVisible ? 'translateY(0)' : 'translateX(-48px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                    '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                        transform: 'none',
                    },
                }}
            >
                {title}
            </Box>

            {children}
        </Box>
    );
}

export default AnimatedTitleAndDescription;